import { supabase } from './supabase';

/** Does this card ask visitors for their details first? */
export const leadCaptureOn = (card, theme) => !!(theme?.force_lead_capture || card?.lead_capture);

/** Send a visitor's details to the cardholder (submit-lead Edge Function). */
export async function submitLead(payload) {
  const { data, error } = await supabase.functions.invoke('submit-lead', { body: payload });
  if (error) {
    let message = 'We could not send your details. Please try again.';
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* keep generic */ }
    throw new Error(message);
  }
  return data;
}

/** Record a card view or contact save. Never blocks the visitor. */
export function trackCardEvent(slug, kind) {
  supabase.rpc('track_card_event', { p_slug: slug, p_kind: kind }).then(({ error }) => {
    if (error) console.warn('track_card_event:', error.message);
  });
}

/** Count views, saves and leads for one card. */
export async function getCardStats(cardId, days = 30) {
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const count = (kind) =>
    supabase.from('card_events').select('id', { count: 'exact', head: true })
      .eq('card_id', cardId).eq('kind', kind).gte('created_at', since)
      .then(({ count: n, error }) => (error ? null : n ?? 0));
  const [views, saves, leads] = await Promise.all([count('view'), count('save'), count('lead')]);
  return { views, saves, leads, days };
}

/** Leads for the signed-in person's own cards (userId) or a whole company (orgId). */
export async function getLeads({ userId = null, orgId = null, cardId = null } = {}) {
  let q = supabase
    .from('leads')
    .select(`id, created_at, full_name, email, phone, company, note, card_id, org_id, cards${userId ? '!inner' : ''}(full_name, slug, user_id)`)
    .order('created_at', { ascending: false })
    .limit(1000);
  if (userId) q = q.eq('cards.user_id', userId);
  if (orgId) q = q.eq('org_id', orgId);
  if (cardId) q = q.eq('card_id', cardId);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function deleteLead(id) {
  const { error } = await supabase.from('leads').delete().eq('id', id);
  if (error) throw error;
}

const csvCell = (v) => {
  if (typeof v === 'number') return String(v); // numbers (e.g. -2.04) are safe as they are
  let s = String(v ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`; // stop spreadsheet formula injection
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Rows as a CSV file download (Excel-friendly UTF-8). */
export function downloadCsv(header, rows, filename) {
  const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Leads as a CSV file download. */
export function downloadLeadsCsv(leads, filename = 'carduzo-leads.csv') {
  const header = ['Date', 'Name', 'Email', 'Phone', 'Company', 'Note', 'Card'];
  const rows = leads.map((l) => [
    new Date(l.created_at).toISOString().replace('T', ' ').slice(0, 16),
    l.full_name, l.email, l.phone, l.company, l.note, l.cards?.full_name,
  ]);
  downloadCsv(header, rows, filename);
}
